import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { inputsLength } from '@/domains/auth/constants/validation';
import AuthEvents from '@/domains/auth/events';
import { AuthPageContainer } from '@/domains/auth/layouts/AuthPageContainer/AuthPageContainer';
import { forgotPassword } from '@/domains/auth/store/authSlice';
import { selectAuthForgotPasswordIsLoading } from '@/domains/auth/store/selectors';
import { FormInput } from '@/components/form/controlled/FormInput/FormInput';
import { NECESSITY_TO_RESET_PASSWORD } from '@/constants/texts';
import { HttpActionCodes } from '@/enums/HttpActionCodes';
import { useEventEffect } from '@/hooks/useEventEffect';
import { useUrlQueryParamsEffect } from '@/hooks/useUrlQueryParamsEffect';
import { RootPaths } from '@/routes/paths';
import SnackService from '@/services/SnackService/SnackService';
import { useDispatch, useSelector } from '@/store/hooks';
import { AdaptiveButton, Form, Options, Title } from './ForgotPassword.styles';
import { validationSchema } from './validationSchema';

const defaultValues = {
  username: '',
};

type FormValues = typeof defaultValues;

export const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const isLoading = useSelector(selectAuthForgotPasswordIsLoading);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const username = watch('username');

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    dispatch(
      forgotPassword({
        login: data.username,
      }),
    );
  };

  useEffect(() => {
    clearErrors('username');
  }, [username, clearErrors]);

  useEventEffect(AuthEvents.RedirectToForgotPasswordEmail, () => navigate(RootPaths.FORGOT_PASSWORD_EMAIL));

  useUrlQueryParamsEffect(HttpActionCodes.RedirectToResetPassword, () => {
    SnackService.add({
      type: SnackType.Error,
      title: NECESSITY_TO_RESET_PASSWORD,
    });
  });

  return (
    <AuthPageContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Title>
          <Typography data-testid="ForgotPasswordTitle" variant={isSmallScreen ? 'h5' : 'h3'}>
            Restore Password
          </Typography>
          <Typography variant={isSmallScreen ? 'body2' : 'body1'}>
            Welcome Back! Please add your Username below and follow instructions.
          </Typography>
        </Title>
        <FormInput
          name="username"
          control={control}
          label="Username"
          errorMessage={errors.username?.message}
          inputProps={{ maxLength: inputsLength.username.max }}
        />
        <Options>
          <AdaptiveButton
            data-testid="ForgotPasswordRestorePasswordButton"
            size="large"
            type="submit"
            width="fluid"
            disabled={isLoading}>
            RESTORE PASSWORD
          </AdaptiveButton>
        </Options>
      </Form>
    </AuthPageContainer>
  );
};
